import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@mainstage/core-plugin-api';
import { handleFailedResponse } from '@agilelab/plugin-wb-platform-common';
import {
  FeatureRequest,
  FeatureRequestListItem,
  FeatureRequestStatus,
  PaginatedResponse,
  ReceivedFeatureRequestFilters,
  RequesterFeatureRequestFilters,
} from '@agilelab/plugin-wb-builder-common';

export interface FeatureRequestRequesterApi {
  fetchByRequester(
    filters: RequesterFeatureRequestFilters,
  ): Promise<PaginatedResponse<FeatureRequestListItem>>;

  fetchReceived(
    filters: ReceivedFeatureRequestFilters,
  ): Promise<PaginatedResponse<FeatureRequestListItem>>;

  fetchProjectsByRequester(): Promise<string[]>;

  fetchProjectsReceived(): Promise<string[]>;

  fetchDetail(id: string): Promise<FeatureRequest>;

  downloadAttachment(
    featureRequestId: string,
    attachmentId: string,
  ): Promise<Blob>;

  updateStatus(
    featureRequestId: string,
    status: FeatureRequestStatus,
  ): Promise<FeatureRequest>;

  deleteRequest(featureRequestId: string): Promise<void>;
}

export const featureRequestRequesterApiRef =
  createApiRef<FeatureRequestRequesterApi>({
    id: 'plugin.wb-user-settings.feature-request-requester',
  });

export class FeatureRequestRequesterClient
  implements FeatureRequestRequesterApi
{
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async fetchByRequester(
    filters: RequesterFeatureRequestFilters,
  ): Promise<PaginatedResponse<FeatureRequestListItem>> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const queryParams = new URLSearchParams();
    if (filters.projectUrn) queryParams.set('projectUrn', filters.projectUrn);
    if (filters.status) queryParams.set('status', filters.status);
    if (filters.priority) queryParams.set('priority', filters.priority);
    if (filters.offset !== undefined)
      queryParams.set('offset', filters.offset.toString());
    if (filters.limit !== undefined)
      queryParams.set('limit', filters.limit.toString());

    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/feature-requests/by-requester?${queryParams}`,
    );
    await handleFailedResponse(response);
    return response.json();
  }

  async fetchReceived(
    filters: ReceivedFeatureRequestFilters,
  ): Promise<PaginatedResponse<FeatureRequestListItem>> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const queryParams = new URLSearchParams();
    if (filters.projectUrn) queryParams.set('projectUrn', filters.projectUrn);
    if (filters.status) queryParams.set('status', filters.status);
    if (filters.priority) queryParams.set('priority', filters.priority);
    if (filters.offset !== undefined)
      queryParams.set('offset', filters.offset.toString());
    if (filters.limit !== undefined)
      queryParams.set('limit', filters.limit.toString());

    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/feature-requests/received?${queryParams}`,
    );
    await handleFailedResponse(response);
    return response.json();
  }

  async fetchProjectsByRequester(): Promise<string[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/feature-requests/by-requester/projects`,
    );
    await handleFailedResponse(response);
    return response.json();
  }

  async fetchProjectsReceived(): Promise<string[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/feature-requests/received/projects`,
    );
    await handleFailedResponse(response);
    return response.json();
  }

  async fetchDetail(id: string): Promise<FeatureRequest> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/feature-requests/${encodeURIComponent(id)}`,
    );
    await handleFailedResponse(response);
    return response.json();
  }

  async downloadAttachment(
    featureRequestId: string,
    attachmentId: string,
  ): Promise<Blob> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/feature-requests/${encodeURIComponent(
        featureRequestId,
      )}/attachments/${encodeURIComponent(attachmentId)}/download`,
    );
    await handleFailedResponse(response);
    return response.blob();
  }

  async updateStatus(
    featureRequestId: string,
    status: FeatureRequestStatus,
  ): Promise<FeatureRequest> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/feature-requests/${encodeURIComponent(
        featureRequestId,
      )}/status`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      },
    );
    await handleFailedResponse(response);
    return response.json();
  }

  async deleteRequest(featureRequestId: string): Promise<void> {
    const baseUrl = await this.discoveryApi.getBaseUrl('builder');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/feature-requests/${encodeURIComponent(featureRequestId)}`,
      { method: 'DELETE' },
    );
    await handleFailedResponse(response);
  }
}
